import type { Out } from './generated/types'
import { useA, useAction, useActionQuery, useAvailability, useQ } from './hooks'
import { useMe, type User, type Me } from './auth'

export const UsersA = {
  list: 'users.list',
  invite: 'users.invite',
  setRoles: 'users.set_roles',
  disable: 'users.disable',
  enable: 'users.enable',
  resetMfa: 'users.reset_mfa',
  reinvite: 'users.reinvite',
  /** Staff directory for pickers (any staff with tasks.manage). */
  directory: 'users.directory',
  /** Permission catalogue (groups, roles, sensitive/privileged flags) for the access editor. */
  catalog: 'users.permission_catalog',
  accessGet: 'users.access_get',
  accessSet: 'users.access_set',
} as const

export type PermissionCatalog = Out<'users.permission_catalog'>
export type UserAccess = Out<'users.access_get'>
export type Effect = 'grant' | 'deny'

export const usePermissionCatalog = () => useQ(UsersA.catalog, {}, { staleTime: 10 * 60_000 })
export const useUserAccess = (userId: string) => useQ(UsersA.accessGet, { user_id: userId })
/** Replaces the whole override set (grants and denies) of a user. */
export const useSetAccess = () => useA(UsersA.accessSet)

export interface InviteResult {
  user: User
  invite_url: string
}

export const useUsers = (query = '') => useActionQuery<User[]>(UsersA.list, { query })
export const useInviteUser = () => useAction<{ email: string; full_name: string; roles: string[] }, InviteResult>(UsersA.invite, { formErrors: true })
export const useSetRoles = () => useAction<{ user_id: string; roles: string[] }, User>(UsersA.setRoles)
export const useDisableUser = () => useAction<{ user_id: string }, User>(UsersA.disable)
export const useEnableUser = () => useAction<{ user_id: string }, User>(UsersA.enable)
export const useResetMfa = () => useAction<{ user_id: string }, { ok: boolean }>(UsersA.resetMfa)
export const useReinvite = () => useAction<{ user_id: string }, InviteResult>(UsersA.reinvite)

export interface StaffOption {
  id: string
  name: string
  email?: string
  roles?: string[]
}

/**
 * Staff list for assignee/watcher pickers: users.directory (tasks.manage, users.manage or
 * connectors.configure — active staff with names and roles, no contact data). Always includes "me".
 */
export function useStaffOptions(): StaffOption[] {
  const me = useMe().data
  const avail = useAvailability(UsersA.directory)
  const dir = useQ(UsersA.directory, {}, { enabled: avail === 'ok', staleTime: 5 * 60_000 })
  const out = new Map<string, StaffOption>()
  for (const u of dir.data ?? []) out.set(u.id, { id: u.id, name: u.full_name || u.id, roles: u.roles ?? [] })
  for (const m of meOption(me)) if (!out.has(m.id)) out.set(m.id, m)
  return [...out.values()].sort((a, b) => a.name.localeCompare(b.name, 'uk'))
}

function meOption(me: Me | undefined): StaffOption[] {
  return me?.user_id ? [{ id: me.user_id, name: me.name || me.email || 'Я', email: me.email }] : []
}
